import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Calendar as CalendarIcon, Clock, CheckCircle2, AlertCircle, Trash2, Send } from "lucide-react";
import { toast } from "sonner";

const PLATFORM_COLORS = {
  instagram: "#E1306C", facebook: "#3B82F6", linkedin: "#6366F1",
  twitter: "#8B5CF6", google_business: "#A78BFA", youtube: "#EF4444",
  threads: "#94A3B8", pinterest: "#F43F5E",
};

export default function PostCalendar() {
  const [posts, setPosts] = useState([]);
  const [contents, setContents] = useState([]);
  const [month, setMonth] = useState(new Date());
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ content_id: "", platform: "instagram", date: "", time: "10:00" });

  const load = async () => {
    const [{ data: sp }, { data: c }] = await Promise.all([
      api.get("/posts/scheduled"), api.get("/ai/content"),
    ]);
    setPosts(sp); setContents(c);
  };
  useEffect(() => { load(); }, []);

  const byDay = useMemo(() => {
    const m = {};
    posts.forEach((p) => {
      const k = (p.scheduled_at || "").slice(0, 10);
      if (!m[k]) m[k] = [];
      m[k].push(p);
    });
    return m;
  }, [posts]);

  const monthDays = useMemo(() => {
    const y = month.getFullYear(), mo = month.getMonth();
    const first = new Date(y, mo, 1);
    const startWd = first.getDay(); // 0=Sun
    const daysInMonth = new Date(y, mo + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWd; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, mo, d));
    while (cells.length % 7) cells.push(null);
    return cells;
  }, [month]);

  const monthLabel = month.toLocaleString("en", { month: "long", year: "numeric" });
  const fmtKey = (d) => d.toISOString().slice(0, 10);

  const schedule = async () => {
    if (!form.content_id || !form.date) { toast.error("Content + date required"); return; }
    try {
      await api.post("/posts/schedule", {
        content_id: form.content_id, platform: form.platform,
        scheduled_at: `${form.date}T${form.time}:00.000Z`,
      });
      toast.success("Scheduled");
      setShowNew(false);
      setForm({ content_id: "", platform: "instagram", date: "", time: "10:00" });
      await load();
    } catch { toast.error("Schedule failed"); }
  };

  const publishNow = async (id) => {
    await api.post(`/posts/scheduled/${id}/publish-now`);
    toast.success("Published (MOCKED)"); await load();
  };
  const del = async (id) => { await api.delete(`/posts/scheduled/${id}`); await load(); };

  return (
    <div className="space-y-6" data-testid="calendar-root">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2">Post Calendar</div>
          <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Plan the month.</h1>
          <p className="text-white/50 mt-2 text-sm">Visual calendar of every scheduled post. Drop a post on a date, AI publishes when it's time.</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="calendar-new-button" className="brand-gradient px-5 py-3 rounded-lg text-sm font-semibold hover:opacity-90 glow-orange inline-flex items-center gap-2">
          <CalendarIcon className="h-4 w-4" /> Schedule a post
        </button>
      </header>

      <div className="flex items-center justify-between">
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="glass px-3 py-2 rounded-lg text-sm hover:bg-white/5">←</button>
        <div className="font-heading font-bold text-2xl">{monthLabel}</div>
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="glass px-3 py-2 rounded-lg text-sm hover:bg-white/5">→</button>
      </div>

      <div className="glass rounded-2xl p-3 lg:p-5">
        <div className="grid grid-cols-7 gap-2 mb-2 text-[10px] uppercase tracking-[0.2em] text-white/40 text-center">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {monthDays.map((d, i) => {
            const items = d ? (byDay[fmtKey(d)] || []) : [];
            return (
              <div key={i} className={`min-h-[96px] rounded-lg p-2 ${d ? "bg-white/[0.02] border border-white/5" : ""}`}>
                {d && (
                  <>
                    <div className="text-xs text-white/40 font-mono mb-1">{d.getDate()}</div>
                    <div className="space-y-1">
                      {items.slice(0, 3).map((p) => (
                        <div
                          key={p.id}
                          data-testid={`calendar-item-${p.id}`}
                          className="text-[10px] px-1.5 py-1 rounded truncate"
                          style={{ background: (PLATFORM_COLORS[p.platform] || "#6366F1") + "33", borderLeft: `2px solid ${PLATFORM_COLORS[p.platform] || "#6366F1"}` }}
                          title={p.content_snapshot?.headline || p.platform}
                        >
                          {p.status === "published" ? "✓ " : ""}{(p.content_snapshot?.headline || p.platform).slice(0, 18)}
                        </div>
                      ))}
                      {items.length > 3 && <div className="text-[10px] text-white/40">+{items.length - 3} more</div>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming list */}
      <section>
        <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-3">All scheduled</div>
        {posts.length === 0 ? (
          <div className="glass rounded-2xl p-10 text-center text-white/50 text-sm">Nothing scheduled yet.</div>
        ) : (
          <div className="space-y-2">
            {posts.map((p) => (
              <div key={p.id} className="glass rounded-xl p-4 flex items-center justify-between gap-3" data-testid={`scheduled-${p.id}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="h-2 w-2 rounded-full" style={{ background: PLATFORM_COLORS[p.platform] || "#6366F1" }} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{p.content_snapshot?.headline || p.content_snapshot?.body?.slice(0, 60) || "(no headline)"}</div>
                    <div className="text-xs text-white/50 font-mono">{p.platform} · {new Date(p.scheduled_at).toLocaleString()}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-[0.2em] ${p.status === "published" ? "text-green-400" : "text-indigo-400"}`}>{p.status}</span>
                  {p.status !== "published" && (
                    <button onClick={() => publishNow(p.id)} className="glass p-2 rounded-lg hover:bg-white/5" title="Publish now">
                      <Send className="h-3.5 w-3.5 text-white/60" />
                    </button>
                  )}
                  <button onClick={() => del(p.id)} className="p-2 rounded-lg hover:bg-white/5">
                    <Trash2 className="h-3.5 w-3.5 text-white/40" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {showNew && (
        <div className="fixed inset-0 z-50 grid place-items-center p-6 bg-black/80" onClick={() => setShowNew(false)}>
          <div onClick={(e) => e.stopPropagation()} className="glass-strong rounded-2xl p-7 max-w-md w-full">
            <h3 className="font-heading text-xl font-black mb-4">Schedule a post</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/40">Content</label>
                <select value={form.content_id} onChange={(e) => setForm({ ...form, content_id: e.target.value })} data-testid="schedule-content-select" className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm">
                  <option value="">Pick content…</option>
                  {contents.map((c) => (
                    <option key={c.id} value={c.id}>{c.content?.headline || c.topic}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-white/40">Platform</label>
                  <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-3 py-2.5 text-sm">
                    {Object.keys(PLATFORM_COLORS).map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-white/40">Date</label>
                  <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="schedule-date-input" className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-3 py-2.5 text-sm" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-white/40">Time</label>
                  <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-3 py-2.5 text-sm" />
                </div>
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={schedule} data-testid="schedule-submit" className="brand-gradient px-5 py-2.5 rounded-lg text-sm font-semibold hover:opacity-90">Schedule</button>
              <button onClick={() => setShowNew(false)} className="glass px-5 py-2.5 rounded-lg text-sm hover:bg-white/5">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
